import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockListHeader from '../components/GuideMockListHeader';
import GuideMockSelect from '../components/GuideMockSelect';
import GuideMockTextField from '../components/GuideMockTextField';
import GuideMockToggleButtonGroup from '../components/GuideMockToggleButtonGroup';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideNoticeCreateNotice: React.FC = () => {
  return (
    <GuidePageLayout
      title="特定ユーザーに通知を送る"
      description="特定のユーザーに対して個人向け通知（NOTICE）を配信します。"
      breadcrumbs={[{ label: '通知管理', path: '/guide/notice/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '管理者メニューから通知管理画面（通知タブ）を開く' },
            {
              label: '一覧パネル右上の「+」アイコンを押下して登録フォームを開く',
              content: (
                <GuideMockContainer label="リストヘッダー">
                  <GuideMockListHeader createLabel="通知を新規作成" />
                </GuideMockContainer>
              ),
            },
            {
              label: '通知内容を入力する',
              description:
                'フォームタイトルは「通知の新規作成」です。タイトル左の戻る矢印をクリックすると、作成を破棄して一覧に戻ります。',
              content: (
                <GuideMockContainer label="通知作成フォーム">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        通知の新規作成
                      </Typography>
                    </Stack>
                    <Stack spacing={2}>
                      <GuideMockTextField label="タイトル" required />
                      <TextField
                        label="内容"
                        required
                        fullWidth
                        size="small"
                        multiline
                        rows={4}
                        sx={{
                          pointerEvents: 'none',
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        }}
                      />
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <GuideMockTextField label="期限" />
                        </Grid>
                        <Grid size={6}>
                          <GuideMockToggleButtonGroup
                            label="優先度（1: 最高 〜 5: 最低）"
                            options={[1, 2, 3, 4, 5]}
                            value={5}
                          />
                        </Grid>
                      </Grid>
                      <GuideMockTextField label="リンク" placeholder="https://..." />
                      <GuideMockSelect
                        label="対象ユーザー"
                        options={['ユーザーA', 'ユーザーB']}
                        required
                      />
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「作成」ボタンを押下する',
              content: (
                <GuideMockContainer label="フッターボタン">
                  <GuideMockButton label="作成" startIcon={<NoteAddIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="入力項目">
        <GuideTable
          headers={['項目名', '必須', '形式', '備考']}
          rows={[
            ['タイトル', '必須', 'テキスト（最大300桁）', ''],
            ['内容', '必須', 'テキストエリア（最大10000桁）', ''],
            ['対象ユーザー', '必須（1名以上）', 'ユーザー選択（複数可）', ''],
            ['リンク', '任意', 'URL（最大500桁）', '関連する外部ページ'],
            ['期限', '任意', '日時ピッカー', ''],
            ['優先度', '任意', 'ボタン選択（1〜5）', '1（最高）〜5（最低）'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印（タイトル左）', '作成を破棄して一覧画面に戻る'],
            ['作成ボタン', '入力内容で通知を作成する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>対象ユーザーを最低1名は選択する必要があります。</GuideInfoBox>
          <GuideInfoBox>
            期限を設定すると、期限が近づいた際に優先度が自動的に引き上げられます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideNoticeCreateNotice;
